import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { SphereButtonProps } from "@components/common/sphere-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { blue, neutral, NO_TRANSLATE_CLASS } from "@flat-ui";
import { Button, ButtonProps } from "@mui/material";
import { MouseEventHandler, PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom";

interface Props extends Pick<SphereButtonProps, "to" | "target"> {
  /** Callback event after clicking the button */
  onClick: MouseEventHandler;

  /** True when the button should be disabled */
  isDisabled?: boolean;

  /** When set to true, the button will show a loader */
  isLoading?: boolean;

  /** Color for the loading indicator */
  loadingIndicatorColor?: string;

  /** Color for the loading track (the remaining part for the progress) */
  loadingTrackColor?: string;

  /** Text to show as tooltip */
  tooltipText?: ReactNode;

  /** Defines a component to show as an icon at the beginning of the button */
  startIcon?: ButtonProps["startIcon"];

  /** Defines a component to show as an icon at the end of the button */
  endIcon?: ButtonProps["endIcon"];

  /**
   * True when the button should only show the icon and no text attached.
   * It will then make sure the icon is properly centered in the button, otherwise
   * there is a small padding between the icon and the rest of the content.
   */
  hasOnlyIcon?: boolean;

  /** Optional sx properties to override the button style. */
  sx?: ButtonProps["sx"];

  /** Size of the button */
  size?: ButtonProps["size"];

  /** Data test ID for testing */
  dataTestId?: string;

  /** Whether to hide hovering effect */
  shouldHideHoveringEffect?: boolean;

  /** Whether to hide focusing effect */
  shouldHideFocusingEffect?: boolean;

  /** If this property is present the text should not be translated with localize */
  shouldNotTranslate?: boolean;
}

/** Renders text button with custom default color */
export function FaroTextButton({
  onClick,
  isDisabled,
  tooltipText,
  children,
  startIcon,
  endIcon,
  hasOnlyIcon,
  size,
  sx = {},
  to,
  target,
  dataTestId,
  shouldHideHoveringEffect = false,
  shouldHideFocusingEffect = false,
  shouldNotTranslate = false,
  isLoading = false,
  loadingIndicatorColor = neutral[300],
  loadingTrackColor = neutral[0],
}: PropsWithChildren<Props>): JSX.Element {
  // Even if the button is inside a Box with className={NO_TRANSLATE_CLASS}, that's not enough to disable translation
  // of the tooltip. So this is needed:
  const tooltipTitle =
    tooltipText && shouldNotTranslate ? (
      <span className={NO_TRANSLATE_CLASS}>{tooltipText}</span>
    ) : (
      tooltipText
    );

  return (
    <SphereTooltip title={tooltipTitle}>
      <Button
        disabled={isDisabled || isLoading}
        variant="text"
        onClick={onClick}
        disableFocusRipple={true}
        disableRipple={true}
        disableElevation={true}
        disableTouchRipple={true}
        draggable={false}
        LinkComponent={to ? Link : undefined}
        // We need to use this spread operator to avoid passing the `to` prop to the button,
        // because there is a type error in the ButtonProps type.
        {...(to ? { to, target } : {})}
        startIcon={startIcon}
        endIcon={endIcon}
        data-testid={dataTestId}
        size={size || "medium"}
        sx={{
          fontSize: "12px",
          textTransform: "none",
          color: blue[500],
          "&:hover": {
            backgroundColor: shouldHideHoveringEffect
              ? "transparent"
              : neutral[200],
          },
          "&:focus": {
            backgroundColor: shouldHideFocusingEffect
              ? "transparent"
              : neutral[200],
          },
          "& .MuiButton-startIcon": {
            margin: hasOnlyIcon ? "0px" : undefined,
          },
          ...sx,
        }}
      >
        {shouldNotTranslate ? (
          <span className={NO_TRANSLATE_CLASS}>{children}</span>
        ) : (
          children
        )}
        <FaroButtonSpinner
          shouldHide={!isLoading}
          loadingIndicatorColor={loadingIndicatorColor}
          loadingTrackColor={loadingTrackColor}
        />
      </Button>
    </SphereTooltip>
  );
}
